import Link from 'next/link'
import { useRouter } from 'next/router'

import Button from 'components/forms/Button'
import useShoppingBag from 'common/useShoppingBag'
import { AppStoreButton, GooglePlayButton } from 'components/AppDownloadButtons'
import { ButtonType } from '../../../types/global'
import MediaCard from 'components/cards/new/common/MediaCard'
import { TPlanSummary } from 'common/cms-types'
import { FC } from 'react'
import useWindowSize from 'components/util/useWindowSize'
import { Tenant } from 'common/tenant'

const APP_PROMO_IMAGE = {
  alt: 'Customer app screenshot displaying mail interface',
  mobile: {
    src: '/images/new/app-ui-showcase.png',
    dimensions: { width: 361, height: 551 },
  },
  desktop: {
    src: '/images/new/app-ui-showcase-cut.png',
    dimensions: { width: 525, height: 801 },
  },
}

interface FooterContentProps {
  planSummary: TPlanSummary
  onBuyClick: () => void
  className: string
  virtualOfficeHref: string
}

const FooterContent: FC<FooterContentProps> = ({
  planSummary,
  onBuyClick,
  className,
  virtualOfficeHref,
}) => {
  return (
    <div className={className}>
      {!!planSummary && (
        <Button onClick={onBuyClick} className='mt-lg-3 px-22 w-100 w-lg-auto'>
          Buy Now
        </Button>
      )}
      <div className='d-flex gap-3 pt-8 pt-lg-7 justify-content-center justify-content-lg-start'>
        <AppStoreButton />
        <GooglePlayButton />
      </div>
      <div className='text-center text-lg-start'>
        <div className='fs-16 mt-2 lh-22'>
          Requires an active{' '}
          <Link href={virtualOfficeHref}>virtual office</Link> subscription
        </div>
      </div>
    </div>
  )
}

const AppPromoCard = ({
  planSummary,
  tenant = Tenant.UK,
}: {
  planSummary: TPlanSummary
  tenant?: Tenant
}) => {
  const au = tenant === Tenant.AU
  const virtualOfficeHref = au
    ? '/au/virtual-office-sydney'
    : '/virtual-office-london-old-street'
  const router = useRouter()
  const { addPlan } = useShoppingBag()
  const size = useWindowSize()
  const isMobile = !size.isLgOrHigher

  const onBuyClick = () => {
    addPlan(planSummary.slug, 'year', planSummary, ButtonType.APP_PROMO_CARD)
    router.push(au ? '/au/checkout' : '/checkout')
  }

  return (
    <MediaCard
      className='py-8 pt-lg-15 pb-0'
      image={APP_PROMO_IMAGE}
      imageFitPreset='custom'
      customImageStyle={{
        objectFit: 'contain',
        objectPosition: isMobile ? 'bottom center' : 'bottom right',
        height: '100%',
        maxWidth: '100%',
      }}
      imageContainerWidth={{ desktop: '41.666%' }}
      contentClassName='pb-lg-7 pb-4'
      imageClassName='rounded-img w-100 h-100'
      footerContent={
        <FooterContent
          planSummary={planSummary}
          onBuyClick={onBuyClick}
          virtualOfficeHref={virtualOfficeHref}
          className='mb-4 d-lg-none ps-4 ps-lg-20 pe-4 pe-lg-0'
        />
      }
    >
      <div className='ps-4 ps-lg-20 pe-4 pe-lg-0'>
        <h2 className='lh-105 fw-semibold fs-fluid-36-80'>
          Organise, sort and manage your business mail correspondence with our
          <span className='color-pink2'> app.</span>
        </h2>
        <h3 className='fw-semibold mb-2 lh-114 fs-fluid-20-28 pt-1 pt-lg-3'>
          All your mail is processed and scanned by our AI assistant, HaiRo™
          before it gets sent directly to the app, where it's summarised,
          intelligently tagged, and fully searchable.
        </h3>
        <div className='fw-semibold lh-132 fs-fluid-18-22'>
          Whether you're on the go or at your desk, managing your business mail
          has never been easier or more efficient.
        </div>
        <FooterContent
          planSummary={planSummary}
          onBuyClick={onBuyClick}
          virtualOfficeHref={virtualOfficeHref}
          className='d-none d-lg-block'
        />
      </div>
    </MediaCard>
  )
}

export default AppPromoCard
